/**
 * 미니 프로필 시트 (시안 03 N1). 별채대화의 얼굴·이름(받아치기 인용의 이름 포함)을 누르면 뜹니다.
 *
 *   손잡이 → [초상 64 + 호칭 · 소개 · 담당] → 한마디 상자 → 「{멤버}과/와 1:1 대화」 → 「프로필 보기」 → 안내
 *
 * 「1:1 대화」는 별채대화 최근 대화를 그 멤버의 맥락으로 넘기고(POST /chats/:key/from-room) 1:1 로 갑니다.
 * 반대(1:1 → 별채대화)는 없습니다. 안내 문구가 그것을 알려 줍니다 (CLAUDE.md).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { BottomSheet } from './BottomSheet.jsx';
import { Avatar } from './Avatar.jsx';
import { Button } from './Button.jsx';
import { errorMessageOf } from '../api/client.js';
import { openChatFromRoom } from '../api/chat.js';

export function MiniProfileSheet({ memberKey, onClose }) {
    const navigate = useNavigate();
    const member = memberKey ? getMember(memberKey) : null;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    async function startChat() {
        setBusy(true);
        setError(null);
        try {
            await openChatFromRoom(memberKey);
            onClose();
            navigate(`/chat/${memberKey}`, { state: { fromRoom: true } });
        } catch (requestError) {
            setError(errorMessageOf(requestError, '1:1을 열지 못했어요. 잠시 후 다시 시도해 주세요.'));
        } finally {
            setBusy(false);
        }
    }

    return (
        <BottomSheet
            open={Boolean(member)}
            onClose={onClose}
            label={member ? `${member.name} 미니 프로필` : '미니 프로필'}
        >
            {member ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                        <Avatar memberKey={memberKey} size={64} />
                        <div className="flex min-w-0 flex-col gap-0.5">
                            <h2 className="m-0 text-[18px] leading-6 font-bold">{member.name}</h2>
                            <span className="text-label font-normal text-text-secondary">{member.line}</span>
                            <span className="text-[13px] text-label-muted">{member.short}</span>
                        </div>
                    </div>
                    <p className="m-0 rounded-[12px] bg-bg px-3.5 py-3 font-serif text-[16px] leading-[26px]">
                        {member.quote}
                    </p>
                    {error ? (
                        <p role="alert" className="m-0 text-caption text-error">
                            {error}
                        </p>
                    ) : null}
                    <div className="flex flex-col gap-2">
                        <Button fullWidth busy={busy} onClick={startChat}>
                            {withJosa(member.name, '과', '와')} 1:1 대화
                        </Button>
                        <Button
                            variant="secondary"
                            fullWidth
                            onClick={() => {
                                onClose();
                                navigate(`/members/${memberKey}`);
                            }}
                        >
                            프로필 보기
                        </Button>
                    </div>
                    <p className="m-0 text-micro font-normal text-label-muted">
                        최근 별채대화를 이어서 알고 시작해요. 1:1에서 한 얘기는 별채대화에 나오지 않아요.
                    </p>
                </div>
            ) : null}
        </BottomSheet>
    );
}
